import { useCallback, useEffect, useRef, useState } from 'react';

// ── Real API health, for the Support page "Live Status" card ─────────────────
//
// Replaces the hardcoded "99.69% / 124ms / ALL SYSTEMS OPERATIONAL" placeholders.
// Every number here is measured, not typed in.
//
// Source: the admin server's own health endpoint (see the server's
// `app.get('/health')`). REACT_APP_API_BASE_URL points at `<origin>/api/v1`,
// so the probe URL is derived by stripping that suffix. Both the probe URL and
// an optional public status-page link can be overridden by env, which is the
// hook for pointing this at a real uptime monitor (Better Stack, Pingdom,
// Statuspage…) if the client stands one up:
//
//   REACT_APP_STATUS_HEALTH_URL=https://status.example.com/api/health
//   REACT_APP_STATUS_PAGE_URL=https://status.example.com
//
// Honesty note: "uptime" here is availability OBSERVED BY THIS BROWSER since
// the page was opened — it is not a historical SLA figure, and the UI labels it
// as such. A true 30/90-day uptime number can only come from an external
// monitor that polls continuously; wire REACT_APP_STATUS_PAGE_URL to it.

const DEFAULT_INTERVAL_MS = 30_000;
const PROBE_TIMEOUT_MS = 8_000;
// Above this, the API is up but slow enough that users feel it.
const DEGRADED_LATENCY_MS = 1_500;

export const STATUS_PAGE_URL = process.env.REACT_APP_STATUS_PAGE_URL || '';

function resolveHealthUrl() {
  if (process.env.REACT_APP_STATUS_HEALTH_URL) return process.env.REACT_APP_STATUS_HEALTH_URL;
  const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
  // Strip the API version suffix to get the server root, where /health lives.
  return `${base.replace(/\/api\/v\d+\/?$/, '').replace(/\/+$/, '')}/health`;
}

export function useApiHealth({ intervalMs = DEFAULT_INTERVAL_MS, enabled = true } = {}) {
  const [state, setState] = useState({
    status: 'checking',   // 'checking' | 'operational' | 'degraded' | 'down'
    latencyMs: null,      // last successful round-trip
    uptimePct: null,      // observed availability across this session's probes
    probes: 0,
    lastCheckedAt: null,
  });

  // Probe tallies live in a ref so a re-render can't reset the running count.
  const tally = useRef({ total: 0, ok: 0 });
  const mounted = useRef(true);

  const probe = useCallback(async () => {
    const url = resolveHealthUrl();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const startedAt = performance.now();

    let ok = false;
    let latencyMs = null;
    try {
      // cache: 'no-store' so a cached 200 can't masquerade as a live server.
      const res = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      latencyMs = Math.round(performance.now() - startedAt);
      ok = res.ok;
    } catch (_) {
      ok = false; // network error, CORS failure, or timeout — all mean "not reachable"
    } finally {
      clearTimeout(timer);
    }

    tally.current.total += 1;
    if (ok) tally.current.ok += 1;

    if (!mounted.current) return;
    const { total, ok: okCount } = tally.current;
    setState((prev) => ({
      status: ok ? (latencyMs > DEGRADED_LATENCY_MS ? 'degraded' : 'operational') : 'down',
      // Keep the last known good latency visible while the API is down, rather
      // than flashing a meaningless dash.
      latencyMs: ok ? latencyMs : prev.latencyMs,
      uptimePct: (okCount / total) * 100,
      probes: total,
      lastCheckedAt: new Date(),
    }));
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!enabled) return undefined;
    probe();
    const id = setInterval(probe, intervalMs);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [enabled, intervalMs, probe]);

  return { ...state, refresh: probe, statusPageUrl: STATUS_PAGE_URL };
}
